import { isFeatureEnabled } from '@/hooks/use-feature-flag';
import { FEATURE_FLAGS } from '@/hooks/use-feature-flag/constants';
import { Urls } from '@/lib/urls';
import { ApartmentContractConfig } from '@pigello/pigello-matrix';
import { Icons } from '../../icons';
import { ICON } from '../constants';
import type { Menu } from '../types';
import {
  BookOpen,
  Box,
  BriefcaseBusiness,
  Building2,
  Calculator,
  FileChartColumn,
  FileCheck,
  FileMinus,
  Files,
  Home,
  Inbox,
  Layers,
  Mail,
  PaintRoller,
  Scale,
  SendHorizonal,
  TrendingUp,
  User,
  Users,
  Wrench,
} from 'lucide-react';

export const menuData: Menu = {
  userMenu: {
    title: 'Meny',
    isIndex: true,
    key: 'userMenu',
    subMenus: [
      {
        path: Urls.dashboard.home,
        key: 'home',
        title: 'Min arbetsdag',
        icon: <BriefcaseBusiness className={ICON} />,
      },
      {
        icon: <Inbox className={ICON} />,
        title: 'Inkorg',
        key: 'monitor-inbox',
        modelName: 'monitoredinstanceaction',
        subMenus: [
          {
            title: 'Chatt',
            key: 'chat',
            path: Urls.dashboard.inbox.chat(),
          },
          {
            title: 'Delegerade ärenden',
            key: 'pending-errands',
            path: Urls.dashboard.inbox.errands,
            modelNames: ['erranddelegationoffer', 'generalerrand'],
          },
          {
            title: 'Delegerade besiktningar',
            key: 'pending-inspections',
            path: Urls.dashboard.inbox.inspections,
            modelNames: ['erranddelegationoffer', 'inspectionerrand'],
          },
          {
            title: 'Godkännanden',
            modelNames: [
              'apartment',
              'article',
              'verificationgroup',
              'capitalcontributionnotation',
              'industrialpremises',
              'parkingspot',
              'outdoorsection',
              'building',
              'realestate',
            ],
            key: 'monitored-proposals',
            path: Urls.dashboard.inbox.approvals,
          },
          {
            title: 'Genomföranden',
            modelNames: [
              'apartment',
              'article',
              'verificationgroup',
              'capitalcontributionnotation',
              'industrialpremises',
              'parkingspot',
              'outdoorsection',
              'building',
              'realestate',
            ],
            key: 'monitored-performs',
            path: Urls.dashboard.inbox.performs,
          },
          {
            title: 'Signeringar',
            key: 'pending-signings',
            path: Urls.dashboard.inbox.signings,
          },
          {
            title: 'Retroaktiva debiteringar',
            key: 'retroactivity',
            path: Urls.dashboard.inbox.retroactivity,
          },
          {
            title: 'Fördelningkvoter',
            key: 'distribution-quotas',
            path: Urls.dashboard.inbox.distribution,
          },
        ],
      },
    ],
  },
  objects: {
    title: 'Objektsdatabas',
    key: 'objects',
    subMenus: [
      {
        path: Urls.dashboard.companies,
        icon: <Layers className={ICON} />,
        title: 'Bolag',
        modelName: 'company',
        detailModalModelName: 'company',
        subMenus: [],
        key: 'companies',
      },
      {
        path: Urls.dashboard.brfCompanies,
        icon: <Scale className={ICON} />,
        title: 'Bostadsrättsföreningar',
        modelName: 'brfcompany',
        detailModalModelName: 'brfcompany',
        subMenus: [],
        key: 'brfcompanies',
      },
      {
        path: Urls.dashboard.externalclients,
        icon: <BookOpen className={ICON} />,
        title: 'Uppdragsgivare',
        modelName: 'externalclient',
        detailModalModelName: 'externalclient',
        subMenus: [],
        key: 'externalclients',
      },
      {
        title: 'Strukturobjekt',
        key: 'structural',
        icon: <Building2 className={ICON} />,
        modelNames: [
          'region',
          'community',
          'segment',
          'realestate',
          'outdoorarea',
          'building',
          'parkinglot',
          'address',
        ],
        subMenus: [
          {
            key: 'regions',
            path: Urls.dashboard.structural.regions,
            title: 'Regioner',
            modelName: 'region',
            detailModalModelName: 'region',
          },
          {
            key: 'communities',
            path: Urls.dashboard.structural.communities,
            title: 'Områden',
            modelName: 'community',
            detailModalModelName: 'community',
          },
          {
            key: 'segments',
            path: Urls.dashboard.structural.segments,
            title: 'Segment',
            modelName: 'segment',
            detailModalModelName: 'segment',
          },
          {
            key: 'realestates',
            path: Urls.dashboard.structural.realestates,
            title: 'Fastigheter',
            modelName: 'realestate',
            detailModalModelName: 'realestate',
          },
          {
            key: 'outdoorareas',
            path: Urls.dashboard.structural.outdoorareas,
            title: 'Utomhusområden',
            modelName: 'outdoorarea',
            detailModalModelName: 'outdoorarea',
          },
          {
            key: 'buildings',
            path: Urls.dashboard.structural.buildings,
            title: 'Byggnader',
            modelName: 'building',
            detailModalModelName: 'building',
          },
          {
            key: 'parkinglots',
            path: Urls.dashboard.structural.parkinglots,
            title: 'Parkeringsområden',
            modelName: 'parkinglot',
            detailModalModelName: 'parkinglot',
          },
          {
            key: 'addresses',
            path: Urls.dashboard.structural.addresses,
            title: 'Adresser',
            modelName: 'address',
            detailModalModelName: 'address',
          },
        ],
      },
      {
        title: 'Objekt',
        key: 'rentable',
        icon: <Box className={ICON} />,
        modelNames: [
          'apartment',
          'industrialpremises',
          'outdoorsection',
          'parkingspot',
          'commonarea',
          'brfapartment',
        ],
        subMenus: [
          {
            key: 'apartments',
            path: Urls.dashboard.apartments,
            title: 'Lägenheter',
            modelName: 'apartment',
            detailModalModelName: 'apartment',
          },
          {
            key: 'industrialpremises',
            path: Urls.dashboard.industrialPremises,
            title: 'Lokaler',
            modelName: 'industrialpremises',
            detailModalModelName: 'industrialpremises',
          },
          {
            key: 'outdoorsections',
            path: Urls.dashboard.outdoorSections,
            title: 'Utomhussektioner',
            modelName: 'outdoorsection',
            detailModalModelName: 'outdoorsection',
          },
          {
            key: 'parkingspots',
            path: Urls.dashboard.parkingSpots,
            title: 'Fordonsplatser',
            modelName: 'parkingspot',
            detailModalModelName: 'parkingspot',
          },
          {
            key: 'commonareas',
            path: Urls.dashboard.commonAreas,
            title: 'Gemensamma ytor',
            modelName: 'commonarea',
            detailModalModelName: 'commonarea',
          },
          {
            key: 'brf',
            path: Urls.dashboard.brf.brfApartments,
            title: 'Bostadsrätter',
            icon: <Home className={ICON} />,
            modelName: 'brfapartment',
            detailModalModelName: 'brfapartment',
          },
        ],
      },
    ],
  },
  modules: {
    key: 'modules',
    title: 'Moduler',
    subMenus: [
      {
        path: Urls.dashboard.tenants,
        key: 'tenants',
        title: 'Hyresgäster',
        icon: <User className={ICON} />,
        modelName: 'tenant',
        detailModalModelName: 'tenant',
      },
      {
        path: Urls.dashboard.subTenants,
        key: 'subtenants',
        title: 'Kontaktpersoner',
        icon: <Users className={ICON} />,
        modelName: 'subtenant',
        detailModalModelName: 'subtenant',
      },
      {
        path: Urls.dashboard.brfMembers,
        key: 'brfmembers',
        title: 'Medlemmar',
        icon: <Users className={ICON} />,
        modelName: 'tenant',
        additionalModelNames: ['brfapartment'],
      },
      {
        path: Urls.documents.overview,
        key: 'documents',
        title: 'Dokument',
        icon: <Files className={ICON} />,
        modelNames: ['file', 'folder'],
      },
      {
        title: 'Avtal',
        key: 'contracts',
        icon: <FileCheck className={ICON} />,
        modelNames: [
          'apartmentcontract',
          'industrialpremisescontract',
          'parkingspotcontract',
          'outdoorsectioncontract',
          'othercontract',
          'blockcontract',
        ],
        subMenus: [
          {
            key: 'apartmentcontracts',
            path: Urls.dashboard.contracts.apartment,
            title: 'Lägenhetsavtal',
            modelName: 'apartmentcontract',
            detailModalModelName: 'apartmentcontract',
          },
          {
            key: 'industrialpremisescontracts',
            path: Urls.dashboard.contracts.industrialPremises,
            title: 'Lokalavtal',
            modelName: 'industrialpremisescontract',
            detailModalModelName: 'industrialpremisescontract',
          },
          {
            key: 'parkingspotcontracts',
            path: Urls.dashboard.contracts.parkingSpot,
            title: 'Parkeringsavtal',
            modelName: 'parkingspotcontract',
            detailModalModelName: 'parkingspotcontract',
          },
          {
            key: 'outdoorsectioncontracts',
            path: Urls.dashboard.contracts.outdoorSection,
            title: 'Utomhussektionsavtal',
            modelName: 'outdoorsectioncontract',
            detailModalModelName: 'outdoorsectioncontract',
          },
          {
            key: 'othercontracts',
            path: Urls.dashboard.contracts.otherContracts,
            title: 'Övriga avtal',
            modelName: 'othercontract',
            detailModalModelName: 'othercontract',
          },
          {
            key: 'blockcontracts',
            path: Urls.dashboard.contracts.blockContracts,
            title: 'Blockavtal',
            modelName: 'blockcontract',
            detailModalModelName: 'blockcontract',
          },
        ],
      },
      {
        key: 'vacancy-notations',
        title: 'Vakanser',
        icon: <FileMinus className={ICON} />,
        modelNames: [
          'apartmentvacancynotation',
          'industrialpremisesvacancynotation',
          'outdoorsectionvacancynotation',
          'parkingspotvacancynotation',
          'vacancyinvoice',
        ],
        subMenus: [
          {
            key: 'vacancyinvoices',
            path: Urls.dashboard.invoicing.vacancyInvoices,
            title: 'Vakansunderlag',
            detailModalModelName: 'vacancyinvoice',
          },
          {
            key: 'apartment-vacancy-notations',
            path: Urls.dashboard.vacancyContracts.apartment,
            title: 'Lägenhetsvakansavtal',
            modelName: 'apartmentvacancynotation',
            detailModalModelName: 'apartmentvacancynotation',
          },
          {
            key: 'industrial-premises-vacancy-notations',
            path: Urls.dashboard.vacancyContracts.industrialPremises,
            title: 'Lokalvakansavtal',
            modelName: 'industrialpremisesvacancynotation',
            detailModalModelName: 'industrialpremisesvacancynotation',
          },
          {
            key: 'parkingspot-vacancy-notations',
            path: Urls.dashboard.vacancyContracts.parkingSpot,
            title: 'Parkeringsvakansavtal',
            modelName: 'parkingspotvacancynotation',
            detailModalModelName: 'parkingspotvacancynotation',
          },
          {
            key: 'outdoorsection-vacancy-notations',
            path: Urls.dashboard.vacancyContracts.outdoorSection,
            title: 'Utomhussektionsvakansavtal',
            modelName: 'outdoorsectionvacancynotation',
            detailModalModelName: 'outdoorsectionvacancynotation',
          },
        ],
      },
      {
        key: 'digital-signing',
        title: 'Digital signering',
        icon: <Files className={ICON} />,
        modelNames: ['signabledocument'],
        subMenus: [
          {
            title: 'Signeringsprocesser',
            key: 'signing-processes',
            path: Urls.dashboard.digitalSigning.processes(
              ApartmentContractConfig.modelName
            ),
          },
          {
            title: 'Mallar',
            key: 'templates',
            path: Urls.dashboard.digitalSigning.templates,
          },
        ],
      },
      {
        title: 'Hyra & Fakturering',
        key: 'rent',
        icon: <Mail className={ICON} />,
        modelNames: ['invoice'],
        subMenus: [
          // {
          //   key: 'invoiceoverview',
          //   path: Urls.dashboard.invoicing.overview,

          //   title: 'Översikt',
          // },
          {
            key: 'invoicingstatus',
            path: Urls.dashboard.invoicing.status,

            title: 'Aviseringsstatus',
          },
          {
            key: 'faileddeliveries',
            path: Urls.dashboard.invoicing.failedDelivieries,
            title: 'Misslyckade leveranser',
            modelNames: ['notification', 'notificationdistribution'],
          },
          {
            key: 'invoices',
            path: Urls.dashboard.invoicing.invoices,
            title: 'Avier',
            detailModalModelName: 'invoice',
            modelName: 'invoice',
          },
          {
            key: 'non-rental-invoices',
            path: Urls.dashboard.invoicing.nonRentalInvoices,
            title: 'Fakturor',
            detailModalModelName: 'invoice',
          },

          {
            key: 'debthandling',
            path: Urls.dashboard.invoicing.debthandling,
            title: 'Påminnelser & Krav',
            modelName: 'invoice',
          },

          // {
          //   key: 'accounting',
          //   path: Urls.dashboard.invoicing.accounting,
          //   title: 'Bokföring',
          // },
          // {
          //   key: 'accounts',
          //   path: Urls.dashboard.invoicing.accounts,
          //   title: 'Reskontra',
          // },
          {
            key: 'payments',
            path: Urls.dashboard.invoicing.payments,
            title: 'Inbetalningar',
          },
          {
            key: 'autogirofailed',
            path: Urls.dashboard.invoicing.failedAutogiroTransactionAttempts,
            title: 'Autogiro misslyckade',
          },
          {
            key: 'autogiropending',
            path: Urls.dashboard.invoicing.pendingAutogiroTransactionAttempts,
            title: 'Autogiro väntande',
          },
          {
            key: 'verificationGroups',
            path: Urls.dashboard.invoicing.verificationGroups,
            title: 'Verifikationsgrupper',
            modelNames: ['bookkeepingaccountsconfig'],
          },
          {
            key: 'verifications',
            path: Urls.dashboard.invoicing.verifications,
            title: 'Verifikat',
            modelNames: ['verification'],
          },
          {
            key: 'bookkeepingExport',
            path: Urls.dashboard.invoicing.bookkeepingExport,
            title: 'Bokföringsexport',
            modelNames: ['bookkeepingaccountsconfig'],
          },
        ],
      },
      {
        title: 'Teknisk förvaltning',
        key: 'general-errands',

        modelNames: ['generalerrand', 'inspectionerrand', 'roundingoccurrence'],
        icon: <Wrench className={ICON} />,
        subMenus: [
          {
            key: 'status',
            path: Urls.dashboard.technicalmanagement.status,
            title: 'Översikt',
          },
          {
            key: 'errands',
            path: Urls.dashboard.technicalmanagement.errands,
            title: 'Ärenden',
            modelName: 'generalerrand',
          },

          {
            key: 'inspections',
            path: Urls.dashboard.technicalmanagement.inspections,
            title: 'Besiktningar',
            modelName: 'inspectionerrand',
          },
          {
            key: 'rounds',
            path: Urls.dashboard.technicalmanagement.rounds,
            title: 'Ronder',
            modelName: 'roundingoccurrence',
            additionalModelNames: ['roundingsetting'],
          },
          {
            key: 'round-calendar',
            path: Urls.dashboard.technicalmanagement.roundCalendar,
            title: 'Ronderingskalender',
            modelName: 'roundingoccurrence',
            additionalModelNames: ['roundingsetting'],
          },
          {
            key: 'components',
            path: Urls.dashboard.technicalmanagement.components,
            title: 'Komponenter',
          },
        ],
      },
      {
        title: 'Marknad & Annonsering',
        key: 'market',
        icon: <Icons.market className={ICON} />,
        modelNames: ['tenantapplicant', 'apartmentlisting'],
        hidden: !isFeatureEnabled(FEATURE_FLAGS.MARKET),
        subMenus: [
          {
            key: 'status',
            path: Urls.dashboard.market.apartmentListings,
            hidden: !isFeatureEnabled(FEATURE_FLAGS.MARKET),
            title: 'Annonser',
            modelName: 'apartmentlisting',
          },
          {
            title: 'Sökande',
            key: 'applicants',
            path: Urls.dashboard.market.applicants,
            hidden: !isFeatureEnabled(FEATURE_FLAGS.MARKET),
            modelName: 'tenantapplicant',
          },
        ],
      },
      {
        title: 'Rapporter',
        path: Urls.dashboard.reports.available,
        key: 'reports',
        modelNames: ['reportresult', 'reporttemplate'],
        icon: <FileChartColumn className={ICON} />,
      },

      {
        title: 'Utskick',
        key: 'send-outs',
        icon: <SendHorizonal className={ICON} />,
        modelNames: ['sendout', 'targetgroup', 'targetgroupmember'],
        path: Urls.dashboard.sendOuts,
        detailModalModelName: 'sendout',
      },
    ],
  },

  processes: {
    key: 'processes',
    title: 'Processer',
    subMenus: [
      {
        title: 'Debiteringshantering',
        key: 'revenue',
        icon: <Calculator className={ICON} />,
        subMenus: [
          {
            key: 'key1',
            path: Urls.dashboard.revenue.distribution.default,
            title: 'Fördelningsavgifter',
          },
          {
            key: 'key2',
            path: Urls.dashboard.revenue.indexation.default,
            title: 'Indexuppräkning',
            modelNames: ['indexationtable', 'indexationsettingusage'],
          },
          {
            key: 'key3',
            path: Urls.dashboard.revenue.revenueIncrease.overview,
            title: 'Avgiftsändringar',
          },
          {
            key: 'key4',
            path: Urls.dashboard.revenue.taxation.overview,
            title: 'Fastighetsskatt',
            modelNames: ['taxationrecord', 'taxationsubrecord'],
          },
          {
            key: 'key5',
            path: Urls.dashboard.revenue.debitRows.apartment,
            title: 'Massjustera debiteringsrader',
            modelNames: [
              'apartmentrevenuerow',
              'industrialpremisesrevenuerow',
              'outdoorsectionrevenuerow',
              'parkingspotrevenuerow',
              'brfapartmentrevenuerow',
              'apartmentcontractrevenuerow',
              'industrialpremisescontractrevenuerow',
              'outdoorsectioncontractrevenuerow',
              'parkingspotcontractrevenuerow',
              'othercontractrevenuerow',
            ],
          },
          {
            key: 'key6',
            path: Urls.dashboard.revenue.retroactivity.default,
            modelNames: [
              'retroactiveapartmentrevenueproposal',
              'retroactiveindustrialpremisesrevenueproposal',
              'retroactiveparkingspotrevenueproposal',
              'retroactiveoutdoorsectionrevenueproposal',
            ],
            title: 'Retroaktivitet',
          },
          {
            key: 'key7',
            path: Urls.dashboard.revenue.customerEarnings.settings.default,
            modelNames: [
              'customerearningsreport',
              'customerearningsrevenuesetting',
              'customerearningsreportmonitoring',
              'customerearningsrevenuesettingmonitoring',
            ],
            title: 'Omsättningshyra',
          },
        ],
      },
      {
        title: 'IMD',
        key: 'imd',
        icon: <TrendingUp className={ICON} />,
        modelNames: ['sensor', 'sensorspecification', 'pricegroup'],
        subMenus: [
          {
            key: 'sensorspecification',
            path: Urls.dashboard.imd.sensorspecifications,
            title: 'Mätartyper',
          },
          {
            key: 'sensors',
            path: Urls.dashboard.imd.sensors,
            title: 'Sensorer',
          },
          {
            key: 'pricegroups',
            path: Urls.dashboard.imd.pricegroups,
            title: 'Prisgrupper',
          },
          {
            key: 'importimddata',
            path: Urls.dashboard.imd.importdata,
            title: 'Importera mätvärden',
          },
        ],
      },
      {
        title: 'Renoveringar',
        key: 'renovations',
        icon: <PaintRoller className={ICON} />,
        path: Urls.dashboard.renovations.overview,
      },
    ],
  },
};
